import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { toast } from "@krea/shared/design-system/utils/toast";

import { mutationKeyMap } from "@/features/query-client";

import { rejectLenderApplication } from "../../api";
import type { IRejectLenderApplicationInput } from "../../types/lender-application";
import { useOptimisticallyUpdateLenderApplication } from "../use-optimistically-update-lender-application";

interface IRejectApplicationMutationParams {
  lenderCompanyId: number;
  lenderApplicationId: number;
  data: IRejectLenderApplicationInput;
}

type IMutationResult = Awaited<ReturnType<typeof rejectLenderApplication>>;

export const useRejectApplicationMutation = () => {
  const optimisticallyUpdateLenderApplication =
    useOptimisticallyUpdateLenderApplication();

  return useMutation<
    IMutationResult,
    AxiosError,
    IRejectApplicationMutationParams
  >({
    mutationFn: async ({
      data,
      lenderApplicationId,
      lenderCompanyId,
    }: IRejectApplicationMutationParams) => {
      return await rejectLenderApplication(
        lenderCompanyId,
        lenderApplicationId,
        data,
      );
    },
    mutationKey: mutationKeyMap.rejectApplication,
    onSuccess: (response, { lenderApplicationId }) => {
      optimisticallyUpdateLenderApplication(lenderApplicationId, response);
    },
    onError: (_error, { lenderApplicationId }) => {
      toast.error(`Failed rejecting lender application ${lenderApplicationId}`);
    },
  });
};
